import { StripeError } from '@stripe/stripe-js'

import {
  API_APP_NAME,
  Cohort,
  PurchaseProduct,
  RequestMethod,
  UpsellProduct,
} from '~/shared/constants'
import { ApiService } from '~/shared/services'
import {
  IBaseSubscriptionInfoRaw,
  IInAppPurchaseResponseRaw,
  IPrimerClientSessionResponseRaw,
  IPrimerPaymentResponseRaw,
  IPurchaseResponseRaw,
  IPurchaseRetrySubscriptionResponseRaw,
  IRequestOptions,
  IResponseResult,
  ISubscriptionInfoRaw,
} from '~/shared/types'

export class PaymentApi {
  private readonly baseUrl = 'v2/purchases'
  private api: ApiService

  constructor(api: ApiService) {
    this.api = api
  }

  createSubscription({
    uuid,
    trialPeriodDays,
    promoCode,
    cohort,
    planId,
  }: {
    uuid: string
    promoCode?: string
    trialPeriodDays: number
    cohort: Cohort
    planId: number
  }): Promise<IResponseResult<IPurchaseResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        promo_code: promoCode,
        plan_id: planId,
        trial_duration: trialPeriodDays,
        cohort,
      },
    }

    return this.api.makeRequest<IPurchaseResponseRaw>(
      `${this.baseUrl}/stripe/${uuid}/${API_APP_NAME}`,
      options,
    )
  }

  retryPayment({
    uuid,
    stripeError,
  }: {
    uuid: string
    stripeError: StripeError
  }): Promise<IResponseResult<IPurchaseRetrySubscriptionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_error: stripeError,
      },
    }
    return this.api.makeRequest<IPurchaseRetrySubscriptionResponseRaw>(
      `${this.baseUrl}/stripe/${uuid}/${API_APP_NAME}/retry`,
      options,
    )
  }

  makeUpsell({
    uuid,
    productId,
    promoCode,
    planId,
  }: {
    uuid: string
    productId: string
    promoCode?: string
    planId: number
  }): Promise<IResponseResult<IPurchaseResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        description: productId,
        promo_code: promoCode,
        plan_id: planId,
      },
    }

    return this.api.makeRequest<IPurchaseResponseRaw>(
      `${this.baseUrl}/stripe/${uuid}/${API_APP_NAME}/upsell`,
      options,
    )
  }

  makePrimerUpsell({
    uuid,
    planId,
    productId,
    promoCode,
  }: {
    uuid: string
    planId: number
    productId: string
    promoCode?: string
  }): Promise<IResponseResult<IBaseSubscriptionInfoRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        description: productId,
        promo_code: promoCode,
        plan_id: planId,
      },
    }

    return this.api.makeRequest<IBaseSubscriptionInfoRaw>(
      `${this.baseUrl}/${uuid}/${API_APP_NAME}/upsell`,
      options,
    )
  }

  createInAppPurchase({
    uuid,
    planId,
    productName,
  }: {
    uuid: string
    planId: number
    productName?: UpsellProduct | PurchaseProduct
  }) {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        plan_id: planId,
        product_name: productName,
      },
    }

    return this.api.makeRequest<IInAppPurchaseResponseRaw>(
      `${this.baseUrl}/${uuid}/${API_APP_NAME}/inapp`,
      options,
    )
  }

  getPrimerClientSessionToken({
    uuid,
    cohort,
    planId,
    trialPeriodDays,
  }: {
    uuid: string
    cohort: string
    trialPeriodDays?: number
    planId: number
  }): Promise<IResponseResult<IPrimerClientSessionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        cohort,
        plan_id: planId,
        trial_duration: trialPeriodDays,
      },
    }

    return this.api.makeRequest<IPrimerClientSessionResponseRaw>(
      `${this.baseUrl}/primer/manual/${uuid}/${API_APP_NAME}/token`,
      options,
    )
  }

  updatePrimerClientSession({
    uuid,
    paymentId,
    clientToken,
  }: {
    uuid: string
    paymentId?: string
    clientToken: string
  }): Promise<IResponseResult<IPrimerClientSessionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.PATCH,
      body: {
        ...(paymentId && { payment_id: paymentId }),
        client_token: clientToken,
      },
    }
    return this.api.makeRequest<IPrimerClientSessionResponseRaw>(
      `${this.baseUrl}/primer/manual/${uuid}/${API_APP_NAME}/token`,
      options,
    )
  }

  resumePrimerSubscription({
    uuid,
    paymentId,
    resumeToken,
  }: {
    uuid: string
    paymentId: string
    resumeToken: string
  }): Promise<IResponseResult<IPrimerPaymentResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_id: paymentId,
        resume_token: resumeToken,
      },
    }
    return this.api.makeRequest<never>(
      `${this.baseUrl}/primer/manual/${uuid}/${API_APP_NAME}/payment/resume`,
      options,
    )
  }

  createPrimerSubscription({
    uuid,
    token,
  }: {
    uuid: string
    token: string
  }): Promise<IResponseResult<IPrimerPaymentResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_method_token: token,
      },
    }
    return this.api.makeRequest<IPurchaseResponseRaw>(
      `${this.baseUrl}/primer/manual/${uuid}/${API_APP_NAME}/payment`,
      options,
    )
  }

  confirmPrimerSubscription({
    uuid,
    paymentId,
  }: {
    uuid: string
    paymentId: string
  }): Promise<
    IResponseResult<{
      status: boolean
      purchase: ISubscriptionInfoRaw
      error?: boolean
    }>
  > {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_id: paymentId,
      },
    }
    return this.api.makeRequest<never>(
      `${this.baseUrl}/primer/manual/${uuid}/${API_APP_NAME}/confirm`,
      options,
    )
  }
}
